// Scheduled reports: a dashboard sent by email to members of the org, as
// charts in the email or as a PDF.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { api, can } from '../api'
import { ErrorBox, Pill } from '../components'
import { dash, datePresets, type DashboardSpec, type DashSummary, type ReportSpec } from '../dashApi'
import { DataGrid } from '../DataGrid'
import { dt } from '../format'

const schedules = [
  { value: '0 7 * * 1', label: 'Every Monday at 07:00' },
  { value: '0 7 1 * *', label: 'On the 1st of every month at 07:00' },
  { value: '0 7 1 1,4,7,10 *', label: 'Every quarter (1 Jan, Apr, Jul, Oct) at 07:00' },
  { value: '0 7 * * *', label: 'Every day at 07:00' },
]

const scheduleLabel = (cron?: string) => schedules.find((s) => s.value === cron)?.label ?? cron ?? ''

export function Reports() {
  const navigate = useNavigate()
  const q = useQuery({ queryKey: ['reports'], queryFn: () => dash.list('report') })
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const cols = useMemo<ColDef<DashSummary>[]>(
    () => [
      {
        field: 'title',
        flex: 2,
        cellRenderer: ({ data }: ICellRendererParams<DashSummary>) =>
          data ? (
            <span className="cell-two">
              <Link to={`/reports/${data.slug}`}>{data.title}</Link>
              <span className="muted mono">{data.slug}</span>
            </span>
          ) : null,
      },
      { field: 'dashboard', width: 200, cellRenderer: ({ value }: ICellRendererParams<DashSummary, string>) => <Link to={`/dashboards/${value}`} className="mono">{value}</Link> },
      { field: 'schedule', flex: 1, valueFormatter: (p) => scheduleLabel(p.value) },
      { field: 'updated_at', headerName: 'Changed', width: 160, valueFormatter: (p) => dt(p.value) },
    ],
    [],
  )
  return (
    <section className="page">
      <header className="page-h">
        <div>
          <div className="crumbs">
            <Link to="/dashboards">Dashboards</Link> / reports
          </div>
          <h1>Reports</h1>
          <p className="sub">
            A report sends a dashboard on a schedule (Europe/Stockholm) to members of this organisation, with the charts in the email or
            as a PDF. Only members can receive reports; anyone who leaves the organisation stops getting them.
          </p>
        </div>
        {can(me.data, 'editor') && (
          <Link className="btn btn-primary" to="/reports/new">
            New report
          </Link>
        )}
      </header>
      {q.error && <ErrorBox error={q.error} />}
      <DataGrid<DashSummary> rows={q.data} columnDefs={cols} loading={q.isLoading} getRowId={(d) => d.slug}
        searchText={(d) => `${d.title} ${d.slug} ${d.dashboard}`} onOpen={(d) => navigate(`/reports/${d.slug}`)} />
    </section>
  )
}

const blank: ReportSpec = { title: '', dashboard: '', schedule: '0 7 * * 1', recipients: [], format: 'html', enabled: true }

export function ReportPage() {
  const { slug = 'new' } = useParams()
  const isNew = slug === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const editor = can(me.data, 'editor')
  const q = useQuery({ queryKey: ['report', slug], queryFn: () => dash.get('report', slug), enabled: !isNew })
  const saved = (q.data?.versions[0]?.body as ReportSpec | undefined) ?? (isNew ? blank : undefined)
  const [draft, setDraft] = useState<ReportSpec | null>(null)
  const spec = draft ?? saved
  const [newSlug, setNewSlug] = useState('')
  const dashboards = useQuery({ queryKey: ['dashboards'], queryFn: () => dash.list('dashboard') })
  const board = useQuery({ queryKey: ['dashboard', spec?.dashboard], queryFn: () => dash.get('dashboard', spec!.dashboard), enabled: !!spec?.dashboard })
  const boardSpec = board.data?.versions[0]?.body as DashboardSpec | undefined
  const org = useQuery({ queryKey: ['org'], queryFn: api.org })
  const sends = useQuery({ queryKey: ['report-sends', slug], queryFn: () => dash.reportSends(slug), enabled: !isNew, refetchInterval: 15_000 })
  const [preview, setPreview] = useState<string | null>(null)
  const [comment, setComment] = useState('')

  const save = useMutation({
    mutationFn: () => (isNew ? dash.create('report', newSlug, spec, 'created') : dash.save('report', slug, spec, comment)),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['reports'] })
      await qc.invalidateQueries({ queryKey: ['report', r.slug] })
      setDraft(null)
      setComment('')
      if (isNew) navigate(`/reports/${r.slug}`)
    },
  })
  const send = useMutation({ mutationFn: () => dash.sendReport(slug), onSuccess: () => setTimeout(() => sends.refetch(), 4000) })
  const pv = useMutation({ mutationFn: () => dash.preview(slug), onSuccess: setPreview })
  const archive = useMutation({ mutationFn: () => dash.archive('report', slug), onSuccess: () => navigate('/reports') })

  if (q.error) return <ErrorBox error={q.error} />
  if (!spec) return <p className="muted">Loading…</p>
  const set = <K extends keyof ReportSpec>(k: K, v: ReportSpec[K]) => setDraft({ ...spec, [k]: v })
  const custom = !schedules.some((s) => s.value === spec.schedule)
  const members = org.data?.members ?? []
  const dirty = draft !== null

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <div className="crumbs">
            <Link to="/reports">Reports</Link> / <span className="mono">{isNew ? 'new' : slug}</span>
          </div>
          <h1>{spec.title || 'New report'}</h1>
          {!isNew && <p className="sub">{spec.enabled ? <Pill kind="ok">enabled</Pill> : <Pill kind="neutral">paused</Pill>} {scheduleLabel(spec.schedule)}</p>}
        </div>
        {!isNew && (
          <div className="row">
            <button className="btn" disabled={pv.isPending || dirty} onClick={() => pv.mutate()} title={dirty ? 'Save first' : ''}>
              Preview
            </button>
            {editor && (
              <button className="btn" disabled={send.isPending || dirty} onClick={() => send.mutate()}>
                {send.isSuccess ? 'Queued' : 'Send now'}
              </button>
            )}
          </div>
        )}
      </header>
      {[save.error, send.error, pv.error, archive.error].filter(Boolean).map((e, i) => (
        <ErrorBox key={i} error={e as Error} />
      ))}
      <div className="cols-2">
        <div className="card form">
          <div className="form-grid">
            <label>
              Title (the email's subject)
              <input value={spec.title} disabled={!editor} onChange={(e) => set('title', e.target.value)} />
            </label>
            {isNew && (
              <label>
                Slug
                <input className="mono" value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} />
              </label>
            )}
            <label>
              Dashboard
              <select value={spec.dashboard} disabled={!editor} onChange={(e) => setDraft({ ...spec, dashboard: e.target.value, filters: undefined })}>
                <option value="">Choose…</option>
                {(dashboards.data ?? []).map((d) => (
                  <option key={d.slug} value={d.slug}>
                    {d.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Format
              <select value={spec.format} disabled={!editor} onChange={(e) => set('format', e.target.value as ReportSpec['format'])}>
                <option value="html">Charts in the email</option>
                <option value="pdf">PDF attachment</option>
              </select>
            </label>
            <label>
              When
              <select value={custom ? 'custom' : spec.schedule} disabled={!editor} onChange={(e) => set('schedule', e.target.value === 'custom' ? '30 6 * * 1-5' : e.target.value)}>
                {schedules.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
                <option value="custom">Custom (cron)…</option>
              </select>
            </label>
            {custom && (
              <label>
                Cron (minute hour day month weekday)
                <input className="mono" value={spec.schedule} disabled={!editor} onChange={(e) => set('schedule', e.target.value)} />
              </label>
            )}
            {(boardSpec?.filters ?? []).map((f) => (
              <label key={f.id}>
                {f.label || f.id}
                {f.kind === 'date_range' ? (
                  <select value={spec.filters?.[f.id] ?? f.default ?? 'all'} disabled={!editor}
                    onChange={(e) => set('filters', { ...(spec.filters ?? {}), [f.id]: e.target.value })}>
                    {datePresets.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={spec.filters?.[f.id] ?? ''} placeholder="All" disabled={!editor}
                    onChange={(e) => set('filters', { ...(spec.filters ?? {}), [f.id]: e.target.value })} />
                )}
              </label>
            ))}
            <label className="wide">
              Introduction (optional, above the charts)
              <textarea rows={4} value={spec.intro ?? ''} disabled={!editor} onChange={(e) => set('intro', e.target.value || undefined)} />
            </label>
            <label className="check">
              <input type="checkbox" checked={spec.enabled} disabled={!editor} onChange={(e) => set('enabled', e.target.checked)} />
              Enabled (send on the schedule)
            </label>
          </div>
          <div>
            <strong>Recipients</strong>
            <p className="note">Members of {org.data?.org.name ?? 'the organisation'}. Invite people on the Organisation page first.</p>
            {members.map((m) => (
              <label key={m.user_id} className="check">
                <input type="checkbox" disabled={!editor} checked={spec.recipients.map((r) => r.toLowerCase()).includes(m.email.toLowerCase())}
                  onChange={(e) => set('recipients', e.target.checked ? [...spec.recipients, m.email] : spec.recipients.filter((r) => r.toLowerCase() !== m.email.toLowerCase()))} />
                {m.name || m.email} <span className="muted">{m.email}</span>
              </label>
            ))}
            {spec.recipients.filter((r) => !members.some((m) => m.email.toLowerCase() === r.toLowerCase())).map((r) => (
              <p key={r} className="callout warn">
                {r} is no longer a member and will be skipped.
              </p>
            ))}
          </div>
          {editor && (
            <div className="row">
              {!isNew && <input className="grow" placeholder="What changed? (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />}
              <button className="btn btn-primary" disabled={save.isPending || (!dirty && !isNew) || !spec.title || !spec.dashboard || (isNew && !newSlug)} onClick={() => save.mutate()}>
                {isNew ? 'Create report' : 'Save'}
              </button>
              {!isNew && (
                <button className="btn" onClick={() => archive.mutate()}>
                  Archive
                </button>
              )}
            </div>
          )}
        </div>
        {!isNew && (
          <div className="card">
            <h2>Sent</h2>
            {(sends.data ?? []).length === 0 && <p className="muted">Nothing sent yet.</p>}
            <table className="tbl">
              <tbody>
                {(sends.data ?? []).map((s, i) => (
                  <tr key={i}>
                    <td>{dt(s.created_at)}</td>
                    <td>{s.recipient}</td>
                    <td>
                      <Pill kind={s.status === 'sent' ? 'ok' : s.status === 'skipped' ? 'warn' : 'err'}>{s.status}</Pill>
                    </td>
                    <td className="muted">{s.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {preview && (
        <div className="card">
          <div className="row-between">
            <h2>Preview</h2>
            <button className="btn btn-sm" onClick={() => setPreview(null)}>
              Close
            </button>
          </div>
          {/* Sandboxed: no scripts, no same-origin access. */}
          <iframe title="Report preview" sandbox="" srcDoc={preview} style={{ width: '100%', height: 900, border: 0, background: '#F6F5F1', borderRadius: 8 }} />
        </div>
      )}
    </section>
  )
}
